import {
  Children,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EmblaOptionsType, EmblaPluginType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import EmblaStyle from '../../styles/EmblaCarousel.module.css';
import ThumbsButton from './ThumbsButton';
import DotsButton from './DotsButton';

type Props = {
  children: ReactNode;
  thumbsChildren: ReactNode;
  selectedMediaIndex: number;
  autoHeight?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  showDots?: boolean;
};

function Carousel({
  children,
  thumbsChildren,
  selectedMediaIndex = 0,
  loop = true,
  autoHeight = false,
  autoPlay = false,
  showDots = true,
}: Props) {
  const plugins: EmblaPluginType[] = autoPlay ? [Autoplay()] : [];
  const options: EmblaOptionsType = { loop };

  // State
  const [selectedIndex, setSelectedIndex] = useState(selectedMediaIndex);

  // Ref
  const containerRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<HTMLDivElement[]>([]);
  const initialHeightRef = useRef(NaN);

  // Hooks
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel(options, plugins);
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const slideArray = useMemo(() => Children.toArray(children), [children]);
  const slides = useMemo(
    () =>
      slideArray.map((slide, idx) => (
        <div
          ref={(el) => {
            if (el) slideRefs.current[idx] = el;
          }}
          key={`slides-${idx}`}
          className={EmblaStyle.emblaSlide}
        >
          {slide}
        </div>
      )),
    [slideArray]
  );

  const onThumbOrDotClick = useCallback(
    (index: number) => {
      if (!emblaMainApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi]
  );

  const updateHeight = useCallback(
    (index: number) => {
      if (!autoHeight || !containerRef.current) return;
      const target = slideRefs.current[index];
      if (target) {
        containerRef.current.style.height = `${target.clientHeight}px`;
      }
    },
    [autoHeight]
  );

  useEffect(() => {
    if (!emblaMainApi) return;
    emblaMainApi.scrollTo(selectedMediaIndex, true);

    const onSelect = () => {
      const snap = emblaMainApi.selectedScrollSnap();
      setSelectedIndex(snap);
      updateHeight(snap);
      emblaThumbsApi?.scrollTo(snap);
    };

    emblaMainApi.on('select', onSelect);

    return () => {
      emblaMainApi.off('select', onSelect);
    };
  }, [emblaMainApi, emblaThumbsApi, selectedMediaIndex, updateHeight]);

  useEffect(() => {
    const height = slideRefs.current[selectedMediaIndex].clientHeight;
    if (initialHeightRef.current !== height) {
      initialHeightRef.current = height;
      updateHeight(selectedMediaIndex);
    }
  }, [slides, selectedMediaIndex, updateHeight]);

  return (
    <div className={EmblaStyle.embla}>
      <div className={EmblaStyle.viewport} ref={emblaMainRef}>
        <div
          ref={containerRef}
          className={EmblaStyle.container}
          style={{ pointerEvents: 'none' }}
        >
          {slides}
        </div>
        {showDots && (
          <DotsButton
            selectedIndex={selectedIndex}
            dataLength={slideArray.length}
            onClick={onThumbOrDotClick}
          />
        )}
      </div>
      {thumbsChildren !== null && (
        <ThumbsButton
          onClick={onThumbOrDotClick}
          emblaThumbsRef={emblaThumbsRef}
          selectedIndex={selectedIndex}
        >
          {thumbsChildren}
        </ThumbsButton>
      )}
    </div>
  );
}

export default Carousel;
